import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Empty,
  Form,
  Image,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { useApp } from "../../contexts/AppContext";
import { animationApi, skillApi } from "../../services/supabaseApi";
import { entityApi } from "../../services/entityApi";
import {
  AnimationFormData,
  BossAnimation,
  BossSkill,
  MotionType,
} from "../../types";
import {
  assetStatus,
  buildMotionGroups,
  MotionGroup,
  motionPatch,
  motionPending,
  newMotion,
} from "../../utils/motionPresentation";
import "./ResourceTables.css";
const { Text, Title } = Typography;
const motionNames: Record<string, string> = {
  BASIC: "기본",
  LOOP: "반복",
  OTHER: "기타",
};
function AssetSelect({
  value,
  onChange,
  required = false,
  id,
}: {
  id?: string;
  value?: boolean | null;
  onChange?: (v: boolean | null | undefined) => void;
  required?: boolean;
}) {
  const encoded =
    value === undefined
      ? "unset"
      : value === null
        ? "none"
        : value
          ? "done"
          : "pending";
  return (
    <Select
      id={id}
      value={encoded}
      onChange={(v) =>
        onChange?.(
          v === "unset" ? undefined : v === "none" ? null : v === "done",
        )
      }
      options={[
        { value: "pending", label: "준비 중" },
        { value: "done", label: "완료" },
        {
          value: "none",
          label: required ? "미설정 (기존 값)" : "불필요",
          disabled: required,
        },
        { value: "unset", label: "미설정", disabled: true },
      ]}
    />
  );
}
const statusTag = (v: boolean | null | undefined, required = false) => (
  <Tag color={v === true ? "green" : v === false ? "orange" : "default"}>
    {assetStatus(v, required)}
  </Tag>
);

const MotionEffectsTab: React.FC<{
  bossCode?: string;
  entityCode?: string;
}> = ({ bossCode, entityCode }) => {
  const { state } = useApp();
  const [notice, noticeHolder] = message.useMessage();
  const code =
    entityCode ||
    bossCode ||
    state.selectedEntity?.entity_code ||
    state.selectedBoss?.boss_code;
  const singleEntity = Boolean(
    entityCode || (!bossCode && state.selectedEntity),
  );
  const [data, setData] = useState<{
    entities: any[];
    skills: BossSkill[];
    animations: BossAnimation[];
  } | null>(null);
  const [owner, setOwner] = useState(""),
    [search, setSearch] = useState(""),
    [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false),
    [error, setError] = useState(""),
    [saving, setSaving] = useState(false),
    [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState<{
    group: MotionGroup;
    record?: BossAnimation;
  } | null>(null);
  const [form] = Form.useForm();
  const request = useRef(0);
  const load = useCallback(async () => {
    if (!code) return;
    const id = ++request.current;
    setLoading(true);
    setError("");
    try {
      const entities = await entityApi.getAll();
      const scope = entities.filter(
        (e) =>
          e.entity_code === code || (!singleEntity && e.parent_code === code),
      );
      if (!scope.some((e) => e.entity_code === code))
        scope.unshift({ entity_code: code, entity_name: code } as any);
      const results = await Promise.all(
        scope.map(async (e) => {
          const [skills, animations] = await Promise.all([
            skillApi.getByBoss(e.entity_code),
            animationApi.getByBossCode(e.entity_code),
          ]);
          return { skills, animations };
        }),
      );
      if (id !== request.current) return;
      setData({
        entities: scope,
        skills: results.flatMap((r) => r.skills),
        animations: results.flatMap((r) => r.animations),
      });
    } catch (e: any) {
      if (id === request.current)
        setError(e.message || "연출 데이터를 불러오지 못했습니다.");
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [code, singleEntity]);
  useEffect(() => {
    setOwner(code || "");
    setData(null);
    setSearch("");
    setFilter("all");
    setEditor(null);
    load();
    const refresh = () => {
      load();
    };
    window.addEventListener("skillsUpdated", refresh);
    return () => {
      request.current++;
      window.removeEventListener("skillsUpdated", refresh);
    };
  }, [code, load]);
  const groups = buildMotionGroups(
    owner,
    data?.skills || [],
    data?.animations || [],
  );
  const filtered = groups.filter(
    (g) =>
      (filter === "all" ||
        (filter === "pending" && motionPending(g)) ||
        (filter === "missing" && g.missingSkill) ||
        (filter === "empty" && !g.records.length)) &&
      [g.name, g.actionKey, ...g.records.map((r) => r.montage_name)].some((v) =>
        v?.toLowerCase().includes(search.toLowerCase()),
      ),
  );
  const startEdit = (group: MotionGroup, record?: BossAnimation) => {
    setEditor({ group, record });
    form.resetFields();
    form.setFieldsValue(
      record
        ? {
            motion_type: record.motion_type,
            montage_name: record.montage_name,
            has_animation: record.has_animation,
            has_sound_effect: record.has_sound_effect,
            has_vfx: record.has_vfx,
          }
        : {
            motion_type: MotionType.BASIC,
            montage_name: "",
            has_animation: false,
            has_sound_effect: null,
            has_vfx: null,
          },
    );
  };
  const save = async () => {
    if (!editor) return;
    let values: Partial<AnimationFormData>;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      if (editor.record) {
        const patch = motionPatch(editor.record, values);
        if (Object.keys(patch).length)
          await animationApi.update(editor.record.id!, patch);
      } else await animationApi.create(newMotion(editor.group, values));
      notice.success("모션을 저장했습니다.");
      setEditor(null);
      await load();
    } catch (e: any) {
      notice.error(e.message || "저장에 실패했습니다. 입력 내용은 유지됩니다.");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (record: BossAnimation) => {
    setBusy(true);
    try {
      await animationApi.delete(record.id!);
      notice.success("모션을 삭제했습니다.");
      await load();
    } catch (e: any) {
      notice.error(e.message || "삭제에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };
  const upload = async (file: File, group: MotionGroup) => {
    if (!file.type.startsWith("image/")) {
      notice.error("이미지 파일을 선택하세요.");
      return;
    }
    setBusy(true);
    try {
      const url = await animationApi.uploadIcon(
        file,
        group.ownerCode,
        group.actionKey,
      );
      if (group.records.length)
        await Promise.all(
          group.records.map((r) =>
            animationApi.update(r.id!, { icon_url: url }),
          ),
        );
      else
        await animationApi.create({ ...newMotion(group, {}), icon_url: url });
      notice.success("아이콘을 저장했습니다.");
      await load();
    } catch (e: any) {
      notice.error(e.message || "아이콘 저장에 실패했습니다.");
      await load();
    } finally {
      setBusy(false);
    }
  };
  const groupStatus = (
    g: MotionGroup,
    field: "has_animation" | "has_sound_effect" | "has_vfx",
  ) => {
    if (!g.records.length) return <Text type="secondary">미등록</Text>;
    if (g.records.every((r) => r[field] === g.records[0][field]))
      return statusTag(g.records[0][field], field === "has_animation");
    const unnecessary =
      field === "has_animation"
        ? 0
        : g.records.filter((r) => r[field] === null).length;
    const completed = g.records.filter((r) => r[field] === true).length;
    const pending = g.records.filter((r) => r[field] === false).length;
    const unset = g.records.filter(
      (r) =>
        r[field] === undefined ||
        (field === "has_animation" && r[field] === null),
    ).length;
    if (g.records.every((r) => r[field] === null) && field !== "has_animation")
      return statusTag(null);
    return (
      <Space direction="vertical" size={0}>
        {completed > 0 && <Text type="success">완료 {completed}</Text>}
        {unnecessary > 0 && <Text type="secondary">불필요 {unnecessary}</Text>}
        {pending > 0 && <Text type="warning">준비 중 {pending}</Text>}
        {unset > 0 && <Text type="secondary">미설정 {unset}</Text>}
      </Space>
    );
  };
  const details = (group: MotionGroup) => (
    <div className="resource-details">
      <Space wrap>
        <Text code copyable>
          {group.actionKey}
        </Text>
        <Text type="secondary">
          소속:{" "}
          {data?.entities.find((e) => e.entity_code === group.ownerCode)
            ?.entity_name || group.ownerCode}
        </Text>
      </Space>
      {group.missingSkill && (
        <Alert
          style={{ margin: "12px 0" }}
          type="warning"
          showIcon
          message="현재 스킬 목록에서 찾을 수 없는 연결입니다."
          description="기존 키와 모션은 유지됩니다. 모션 정보만 편집할 수 있으며 연결 키는 자동 변경하지 않습니다."
        />
      )}
      <div className="resource-toolbar" style={{ marginTop: 16 }}>
        <Space>
          <Button
            icon={<PlusOutlined />}
            onClick={() => startEdit(group)}
            disabled={busy}
          >
            모션 추가
          </Button>
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={(file) => {
              upload(file, group);
              return false;
            }}
            disabled={busy}
          >
            <Button icon={<UploadOutlined />} disabled={busy}>
              아이콘 변경
            </Button>
          </Upload>
        </Space>
        <Text type="secondary">
          아이콘은 이 몬스터의 선택한 스킬에만 적용됩니다.
        </Text>
      </div>
      <Table
        size="small"
        tableLayout="fixed"
        rowKey={(r: BossAnimation) => String(r.id)}
        dataSource={group.records}
        pagination={false}
        scroll={{ x: 750 }}
        locale={{
          emptyText: "등록된 모션이 없습니다. 모션 추가로 시작하세요.",
        }}
        columns={[
          {
            title: "모션",
            render: (_: any, r: BossAnimation) => (
              <div className="resource-name">
                <Text strong>{r.montage_name || "몽타주 미지정"}</Text>
                <Text type="secondary">
                  {motionNames[r.motion_type] || r.motion_type} · 순서{" "}
                  {r.sort_order}
                </Text>
              </div>
            ),
          },
          {
            title: "애니메이션",
            width: 110,
            render: (_: any, r: BossAnimation) =>
              statusTag(r.has_animation, true),
          },
          {
            title: "효과음",
            width: 100,
            render: (_: any, r: BossAnimation) => statusTag(r.has_sound_effect),
          },
          {
            title: "시각 효과",
            width: 100,
            render: (_: any, r: BossAnimation) => statusTag(r.has_vfx),
          },
          {
            title: "작업",
            width: 160,
            render: (_: any, r: BossAnimation) => (
              <Space>
                <Button
                  size="small"
                  disabled={busy || r.id == null}
                  onClick={() => startEdit(group, r)}
                >
                  편집
                </Button>
                <Popconfirm
                  title="이 모션을 삭제할까요?"
                  description={r.montage_name || group.actionKey}
                  onConfirm={() => remove(r)}
                  okText="삭제"
                  cancelText="취소"
                >
                  <Button size="small" danger disabled={busy || r.id == null}>
                    삭제
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <Collapse
        ghost
        items={[
          {
            key: "raw",
            label: "기존 파일명 · 원본 데이터",
            children: (
              <pre className="resource-json">
                {JSON.stringify(group.records, null, 2)}
              </pre>
            ),
          },
        ]}
      />
    </div>
  );
  if (!code) return <Empty description="몬스터를 선택하세요." />;
  return (
    <div className="resource-page">
      {noticeHolder}
      <div className="resource-toolbar">
        <div>
          <Title level={4}>모션 및 효과</Title>
          <Text type="secondary">
            몬스터별 애니메이션·효과음·시각 효과의 준비 상태를 확인하세요.
          </Text>
        </div>
        <Button loading={loading} disabled={busy} onClick={load}>
          새로고침
        </Button>
      </div>
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
        />
      )}
      <Space wrap className="resource-filters">
        <Select
          aria-label="연출 몬스터"
          value={owner || undefined}
          onChange={(value) => {
            setOwner(value);
            setSearch("");
          }}
          style={{ minWidth: 240 }}
          options={(data?.entities || []).map((e) => ({
            value: e.entity_code,
            label: e.entity_name,
          }))}
        />
        <Select
          aria-label="연출 상태"
          value={filter}
          onChange={setFilter}
          style={{ width: 180 }}
          options={[
            { value: "all", label: "모든 상태" },
            { value: "pending", label: "준비 필요" },
            { value: "empty", label: "모션 미등록" },
            { value: "missing", label: "연결 확인 필요" },
          ]}
        />
        <Input.Search
          aria-label="연출 검색"
          placeholder="스킬 · 모션 이름 또는 코드 검색"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 280 }}
        />
      </Space>
      <Space wrap style={{ marginBottom: 18 }}>
        <Tag>기본 동작·스킬 {groups.length}개</Tag>
        <Tag>모션 {groups.reduce((n, g) => n + g.records.length, 0)}개</Tag>
        <Tag color={groups.some((g) => g.missingSkill) ? "orange" : "default"}>
          연결 확인 {groups.filter((g) => g.missingSkill).length}개
        </Tag>
        <Text type="secondary">
          행을 펼치면 모션을 추가하거나 편집할 수 있습니다.
        </Text>
      </Space>
      <Table
        className="resource-table"
        // Nested empty tables measure their container; auto layout feeds that
        // measured width back into this table and grows it on every resize.
        tableLayout="fixed"
        loading={loading}
        rowKey="key"
        dataSource={filtered}
        scroll={{ x: 850 }}
        pagination={{
          defaultPageSize: 15,
          showSizeChanger: true,
          showTotal: (n) => `${n}개`,
        }}
        expandable={{ expandedRowRender: details }}
        columns={[
          {
            title: "동작 / 스킬",
            width: 270,
            render: (_: any, g: MotionGroup) => (
              <Space align="start">
                {g.records.find((r) => r.icon_url)?.icon_url && (
                  <Image
                    width={36}
                    height={36}
                    style={{ objectFit: "contain" }}
                    src={g.records.find((r) => r.icon_url)?.icon_url}
                  />
                )}
                <div className="resource-name">
                  <Text strong>{g.name}</Text>
                  {g.missingSkill ? (
                    <Text type="warning" style={{ overflowWrap: "anywhere" }}>
                      {g.actionKey}
                    </Text>
                  ) : (
                    <Text type="secondary">모션 {g.records.length}개</Text>
                  )}
                </div>
              </Space>
            ),
          },
          {
            title: "애니메이션",
            width: 140,
            render: (_: any, g: MotionGroup) => groupStatus(g, "has_animation"),
          },
          {
            title: "효과음",
            width: 130,
            render: (_: any, g: MotionGroup) =>
              groupStatus(g, "has_sound_effect"),
          },
          {
            title: "시각 효과",
            width: 130,
            render: (_: any, g: MotionGroup) => groupStatus(g, "has_vfx"),
          },
          {
            title: "작업",
            width: 140,
            render: (_: any, g: MotionGroup) => (
              <Button size="small" disabled={busy} onClick={() => startEdit(g)}>
                모션 추가
              </Button>
            ),
          },
        ]}
      />
      <Modal
        forceRender
        open={Boolean(editor)}
        title={editor?.record ? "모션 편집" : "모션 추가"}
        onCancel={() => {
          if (!saving) setEditor(null);
        }}
        onOk={save}
        confirmLoading={saving}
        cancelButtonProps={{ disabled: saving }}
        closable={!saving}
        maskClosable={!saving}
        okText="저장"
        cancelText="취소"
        width={560}
      >
        <p>
          <Text strong>{editor?.group.name}</Text> ·{" "}
          {
            data?.entities.find(
              (e) => e.entity_code === editor?.group.ownerCode,
            )?.entity_name
          }
        </p>
        <Form form={form} layout="vertical">
          <Form.Item
            name="motion_type"
            label="모션 종류"
            rules={[{ required: true, message: "종류를 선택하세요." }]}
          >
            <Select
              options={Object.entries(motionNames).map(([value, label]) => ({
                value,
                label,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="montage_name"
            label="몽타주 이름"
            rules={
              editor?.record && !editor.record.montage_name
                ? []
                : [
                    {
                      required: true,
                      whitespace: true,
                      message: "몽타주 이름을 입력하세요.",
                    },
                  ]
            }
          >
            <Input placeholder="예: AM_Healing_Wind" />
          </Form.Item>
          <Form.Item name="has_animation" label="애니메이션">
            <AssetSelect required />
          </Form.Item>
          <Form.Item name="has_sound_effect" label="효과음">
            <AssetSelect />
          </Form.Item>
          <Form.Item name="has_vfx" label="시각 효과 (VFX)">
            <AssetSelect />
          </Form.Item>
        </Form>
        <Text type="secondary">소속과 스킬 연결은 그대로 유지됩니다.</Text>
      </Modal>
    </div>
  );
};
export default MotionEffectsTab;
